"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Menu,
  Settings,
  Bell,
  Search,
  LogOut,
  User,
  Shield,
} from "lucide-react";
import { useChat } from "@/lib/chat-context";
import { authClient } from "@/lib/auth-client";

const navigation = [
  // { name: "Home", href: "/app", icon: Home },
  // { name: "Feed", href: "/app/feed", icon: MessageSquare },
  // { name: "Chat", href: "/app/chat", icon: MessageSquare },
  // { name: "Resettlement", href: "/app/resettlement", icon: Users }, // Re-added resettlement route now that page exists
  // { name: "Events", href: "/app/events", icon: Calendar },
  // { name: "Training", href: "/app/training", icon: BookOpen },
  // { name: "Evaluations", href: "/app/evaluations", icon: Award },
  // { name: "Video Calls", href: "/app/video-calls", icon: Video },
];

export function MainNav() {
  const pathname = usePathname();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const { data: user } = authClient.useSession();

  const { chatRooms } = useChat();

  // Calculate total unread messages
  const totalUnreadMessages = chatRooms.reduce(
    (total, room) => total + (room.unreadCount || 0),
    0,
  );

  const handleLogout = () => {
    authClient.signOut();
    router.push("/login");
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between">
        <div className="flex items-center gap-6">
          <Link href="/" className="flex items-center gap-2">
            <div className="relative h-8 w-24 bg-primary flex items-center justify-center">
              <Image src="/logo.webp" fill alt="Mahlia Network"/>
            </div>
          </Link>

          {user && (
            <nav className="hidden md:flex items-center gap-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isChat = item.name === "Chat";
                return (
                  <Link key={item.name} href={item.href}>
                    <Button
                      variant={pathname === item.href ? "secondary" : "ghost"}
                      size="sm"
                      className="gap-2 relative"
                    >
                      <Icon className="h-4 w-4" />
                      {item.name}
                      {isChat && totalUnreadMessages > 0 && (
                        <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs">
                          {totalUnreadMessages}
                        </Badge>
                      )}
                    </Button>
                  </Link>
                );
              })}
              {(user?.user.role_id === 1 || user?.user.role_id === 2) && (
                <Link href="/admin">
                  <Button
                    variant={
                      pathname.startsWith("/admin") ? "secondary" : "ghost"
                    }
                    size="sm"
                    className="gap-2"
                  >
                    <Shield className="h-4 w-4" />
                    Admin
                  </Button>
                </Link>
              )}
            </nav>
          )}
        </div>

        <div className="flex items-center gap-2">
          {user?.user ? (
            <>
              <Button variant="ghost" size="icon" className="hidden md:flex">
                <Search className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="h-4 w-4" />
                <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs">
                  3
                </Badge>
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="relative h-8 w-8 rounded-full"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage
                        src={user.user.image || "/placeholder.svg"}
                        alt={user.user.name}
                      />
                      <AvatarFallback>{user.user.name[0]}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {user.user.name} {user.user.name}
                      </p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user.user.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => router.push("/profile")}>
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => router.push("/settings")}>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </DropdownMenuItem>
                  {(user.user.role_id === 1 || user.user.role_id === 2) && (
                    <DropdownMenuItem onClick={() => router.push("/admin")}>
                      <Shield className="mr-2 h-4 w-4" />
                      <span>Admin Panel</span>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden">
                    <Menu className="h-4 w-4" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-80">
                  <div className="flex flex-col gap-4 mt-8">
                    {navigation.map((item) => {
                      const Icon = item.icon;
                      const isChat = item.name === "Chat";
                      return (
                        <Link
                          key={item.name}
                          href={item.href}
                          onClick={() => setIsOpen(false)}
                        >
                          <Button
                            variant={
                              pathname === item.href ? "secondary" : "ghost"
                            }
                            className="w-full justify-start gap-2 relative"
                          >
                            <Icon className="h-4 w-4" />
                            {item.name}
                            {isChat && totalUnreadMessages > 0 && (
                              <Badge className="ml-auto">
                                {totalUnreadMessages}
                              </Badge>
                            )}
                          </Button>
                        </Link>
                      );
                    })}
                    {(user.user.role_id === 1 || user.user.role_id === 2) && (
                      <Link href="/admin" onClick={() => setIsOpen(false)}>
                        <Button
                          variant={
                            pathname.startsWith("/admin")
                              ? "secondary"
                              : "ghost"
                          }
                          className="w-full justify-start gap-2"
                        >
                          <Shield className="h-4 w-4" />
                          Admin Panel
                        </Button>
                      </Link>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden">
                    <Menu className="h-4 w-4" />
                    NNNNNNNNNNNNNNNN
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" forceMount>
                  <DropdownMenuItem asChild>
                    <Link href="/login">Sign In</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/register">Sign Up</Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Link href="/login">
                <Button variant="ghost">Sign In</Button>
              </Link>
              <Link href="/register">
                <Button>Sign Up</Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
